import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observable, reaction, runInAction } from 'mobx';
import { SHELL_ALL_WORK_AREAS_SCOPE } from '@featit/shared/shell-paths';
import { useStores } from '@/hooks/useStores';
import { NAMESPACE_SCOPE_PREFIX, type ProjectStore } from '@/stores/ProjectStore';
import { onShellEvent, postShellMessage } from './shellBridge';
import { refreshWorkAreaNavigationPreferences } from '@/features/work-areas/useWorkAreaNavigationPreferences';
import {
  appScopeSwitcherActive,
  screenFollowTarget,
  tabRootFollowTarget,
  workAreaOfPath,
} from './appScope';

/** How long "Now in X · Undo" stays (AC-19.6). */
export const APP_SCOPE_NOTICE_MS = 6_000;

export interface IAppScopeNotice {
  /**
   * `moved`: a link took the app into another work area (AC-19.6). `unavailable`: I-1213. `gone`:
   * outside the iPhone app, the work area this window was in went away (I-1876, MWI-27).
   */
  readonly kind: 'moved' | 'unavailable' | 'gone';
  /** The work area the notice is about; `null` when its name was never known here. */
  readonly name: string | null;
  /** Only a move can be undone: the work area an unavailable scope named is not there to go back to. */
  readonly undo: (() => void) | null;
  readonly dismiss: () => void;
}

/**
 * F-0530 (AC-19.1): the person chose a scope in this web view — stored, applied here, and told to
 * the shell, which tells every other web view (`workAreaScope`).
 */
export function chooseAppScopeEverywhere(projectStore: ProjectStore, scope: string): string | null {
  const previous = projectStore.chooseAppScope(scope);
  postShellMessage({ type: 'setWorkAreaScope', scope });
  return previous;
}

/**
 * RD-29 (I-1482): the address this window heard a scope chosen on, while it may still follow it by
 * `screenFollowTarget`. Read by `addressFollowsAppScope`, so a screen opened from More (Tasks for me
 * under All) is not read as All in the frames before its navigation lands. Dropped once the address
 * changes; a later visit to the same address is the person's own and reads as its scope again.
 */
const followingFrom = observable.box<string | null>(null);

/** The address a heard choice is still moving this window from, or `null` (RD-29). */
export function appScopeFollowingFrom(): string | null {
  return followingFrom.get();
}

/**
 * A scope naming a work area the list here does not hold, once the list has answered. A list still
 * being read answers by itself; All work areas and a namespace's scope name no work area.
 */
export function scopeIsUnreadWorkArea(
  projectStore: Pick<ProjectStore, 'projectMap' | 'hasWorkAreaAnswer' | 'loading'>,
  scope: string,
): boolean {
  if (scope === SHELL_ALL_WORK_AREAS_SCOPE || scope.startsWith(NAMESPACE_SCOPE_PREFIX))
    return false;
  return projectStore.hasWorkAreaAnswer && !projectStore.loading && !projectStore.projectMap[scope];
}

/**
 * F-0530 (AC-19.1, AC-19.6): keeps this web view on the app's one scope. Only in the iPhone app with
 * `workAreaSwitcher` (`appScopeSwitcherActive`); a no-op everywhere else.
 *
 * - A `workAreaScope` event is the scope chosen elsewhere: this window takes it.
 * - A tab root that does not show the scope moves to the root that does, on the event, on a tab
 *   opened later, and once the work-area list knows the chosen work area.
 * - I-1363: any other screen about a scope (Work area settings, Home, Parts, opened from More) moves
 *   to the same page under the scope, on the event only (`screenFollowTarget`), so a choice made in
 *   one tab or in Settings reaches every tab, already loaded or not, with no relaunch.
 * - `workAreasChanged` (the edit view closed): the work areas, the namespaces and the order are read
 *   again.
 * - A link the shell opens (`navigate`) into another work area switches the scope, with "Now in X ·
 *   Undo". Under All every work area is in scope, so a link there changes nothing.
 */
export function useAppScope(): IAppScopeNotice | null {
  const { projectStore, namespaceStore } = useStores();
  const location = useLocation();
  const navigate = useNavigate();
  const active = appScopeSwitcherActive();
  const [notice, setNotice] = useState<
    | { kind: 'moved'; name: string; previous: string }
    | { kind: 'unavailable' | 'gone'; name: string | null }
    | null
  >(null);

  /*
    I-1363: a choice this web view heard, for the screen it was on. Kept until the scope's work area
    is known here, and dropped once the person goes elsewhere: a later address is theirs.
  */
  const whereRef = useRef({ pathname: location.pathname, search: location.search });
  whereRef.current = { pathname: location.pathname, search: location.search };
  const [followRequest, setFollowRequest] = useState<{ pathname: string } | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    return onShellEvent((event) => {
      if (event.type === 'workAreaScope') {
        // Before the scope moves, so the commit that sees the new scope sees this too (RD-29).
        runInAction(() => followingFrom.set(whereRef.current.pathname));
        if (event.scope !== projectStore.appScope) projectStore.chooseAppScope(event.scope);
        setFollowRequest({ pathname: whereRef.current.pathname });
        // AC-19.11.5: a work area this web view has not read yet — created a moment ago in
        // another one — is read now, so this tab follows at once and not on its next refresh.
        if (scopeIsUnreadWorkArea(projectStore, event.scope)) void projectStore.fetchProjects();
        return;
      }
      if (event.type === 'workAreasChanged') {
        // AC-19.4: the edit view closed somewhere — a rename, a reorder or a new work area.
        void projectStore.fetchProjects();
        void namespaceStore?.load();
        void refreshWorkAreaNavigationPreferences();
        return;
      }
      if (event.type !== 'navigate') return;
      const target = workAreaOfPath(event.path, projectStore.projects);
      const scope = projectStore.appScope;
      if (!target || !scope || scope === SHELL_ALL_WORK_AREAS_SCOPE || scope === target.id) return;
      const previous = chooseAppScopeEverywhere(projectStore, target.id);
      if (previous) setNotice({ kind: 'moved', name: target.name, previous });
    });
  }, [active, namespaceStore, projectStore]);

  /*
    I-1213: a stored scope whose work area the list no longer holds — deleted, or the person's
    access removed — falls back to All work areas, for every tab and the next launch, and says so
    once: the stored scope is then All, so no later load finds it missing again.
  */
  useEffect(() => {
    if (!active) return undefined;
    return reaction(
      () => projectStore.unavailableAppScope,
      (unavailable) => {
        if (!unavailable) return;
        projectStore.acknowledgeUnavailableAppScope();
        if (projectStore.appScope !== unavailable.id) return;
        chooseAppScopeEverywhere(projectStore, SHELL_ALL_WORK_AREAS_SCOPE);
        setNotice({ kind: 'unavailable', name: unavailable.name });
      },
      { fireImmediately: true },
    );
  }, [active, projectStore]);

  /*
    I-1876 (MWI-27): outside the iPhone app, the work area this window was in is not in the list
    any more. The store has already moved the selection on; this says why, once. A 403 on its reads
    is answered here, never by signing the person out.
  */
  useEffect(() => {
    if (active) return undefined;
    return reaction(
      () => projectStore.unavailableWorkArea,
      (gone) => {
        if (!gone) return;
        projectStore.acknowledgeUnavailableWorkArea();
        setNotice({ kind: 'gone', name: gone.name });
      },
      { fireImmediately: true },
    );
  }, [active, projectStore]);

  const pathname = location.pathname;
  /*
    A layout effect, so a tab root opened on another scope's address moves before its first paint
    (the captain, 2026-10-06: a screen that sends you elsewhere decides before it paints).
  */
  useLayoutEffect(() => {
    if (!active) return undefined;
    // A reaction, so a scope that arrives while the list is still loading moves the root once the
    // list knows the work area.
    return reaction(
      () => tabRootFollowTarget(pathname, projectStore.appScope, projectStore.projects),
      (target) => {
        if (target) navigate(target, { replace: true });
      },
      { fireImmediately: true },
    );
  }, [active, navigate, pathname, projectStore]);

  /*
    I-1363: the screen about a scope follows the choice, once the list knows its work area. A layout
    effect, as the tab root's: the old work area's page is never painted under the new scope.
  */
  const search = location.search;
  useLayoutEffect(() => {
    // RD-29: once the address has moved, it is the scope again.
    if (followingFrom.get() !== null && followingFrom.get() !== pathname) {
      runInAction(() => followingFrom.set(null));
    }
  }, [pathname]);
  useLayoutEffect(() => {
    if (!active || !followRequest) return undefined;
    if (followRequest.pathname !== pathname) {
      setFollowRequest(null);
      return undefined;
    }
    return reaction(
      () => {
        const scope = projectStore.appScope;
        const known =
          scope === null ||
          scope === SHELL_ALL_WORK_AREAS_SCOPE ||
          projectStore.projects.some((project) => project.id === scope);
        return known
          ? { target: screenFollowTarget(pathname, search, scope, projectStore.projects) }
          : null;
      },
      (decided) => {
        if (!decided) return;
        setFollowRequest(null);
        if (decided.target) navigate(decided.target, { replace: true });
        // Staying here, the address is the scope again; a move clears it when it lands.
        else runInAction(() => followingFrom.set(null));
      },
      { fireImmediately: true },
    );
  }, [active, followRequest, navigate, pathname, projectStore, search]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), APP_SCOPE_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const dismiss = useCallback(() => setNotice(null), []);
  if (!notice) return null;
  if (notice.kind !== 'moved') {
    return { kind: notice.kind, name: notice.name, dismiss, undo: null };
  }
  const previous = notice.previous;
  return {
    kind: 'moved',
    name: notice.name,
    dismiss,
    undo: () => {
      chooseAppScopeEverywhere(projectStore, previous);
      setNotice(null);
    },
  };
}
